import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';

export const sanitizeOtpValue = (value, length = 6) => value.replace(/\D/g, '').slice(0, length);

const OtpSlotField = forwardRef(function OtpSlotField(
  {
    value,
    onChange,
    length = 6,
    label = 'Verification code',
    describedBy,
    disabled = false,
    autoFocus = false,
    invalid = false,
    name = 'verification-code',
  },
  forwardedRef
) {
  const inputId = useId();
  const inputRef = useRef(null);
  const pendingPastedValueRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useImperativeHandle(forwardedRef, () => inputRef.current);

  const digits = useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [length, value]
  );

  const focusInput = useCallback(() => {
    if (!inputRef.current || disabled) return;
    inputRef.current.focus();
    const end = value.length;
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange?.(end, end);
    });
  }, [disabled, value.length]);

  useEffect(() => {
    if (!autoFocus) return;
    focusInput();
  }, [autoFocus, focusInput]);

  const commitValue = (nextValue) => {
    const sanitizedValue = sanitizeOtpValue(nextValue, length);
    const appendedValue = sanitizeOtpValue(nextValue.slice(value.length), length);

    if (
      value.length > 0
      && sanitizedValue.length === length
      && appendedValue.length === length
    ) {
      onChange(appendedValue);
      return;
    }

    onChange(sanitizedValue);
  };

  const activeIndex = Math.min(value.length, length - 1);

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="sr-only">
        {label}
      </label>

      <div
        className="relative"
        onClick={focusInput}
        onMouseUp={(event) => {
          event.preventDefault();
          focusInput();
        }}
      >
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          enterKeyHint="done"
          pattern="[0-9]*"
          name={name}
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
          className="absolute inset-0 z-10 h-full w-full cursor-text opacity-0"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(event) => {
            if (pendingPastedValueRef.current !== null) {
              const pastedValue = pendingPastedValueRef.current;
              pendingPastedValueRef.current = null;
              commitValue(pastedValue);
              return;
            }
            commitValue(event.target.value);
          }}
          onPaste={(event) => {
            const pasted = event.clipboardData?.getData('text') ?? '';
            if (!pasted) return;
            event.preventDefault();
            const sanitizedValue = sanitizeOtpValue(pasted, length);
            pendingPastedValueRef.current = sanitizedValue;
            commitValue(sanitizedValue);
          }}
          onKeyDown={(event) => {
            if (event.key === ' ') {
              event.preventDefault();
            }
          }}
        />

        <div
          aria-hidden="true"
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
        >
          {digits.map((digit, index) => {
            const isActive = isFocused && index === activeIndex;
            const hasValue = digit !== '';
            const slotClasses = [
              'flex h-14 items-center justify-center rounded-xl border bg-[var(--color-surface-input)] text-xl font-semibold tabular-nums transition-colors duration-150 sm:h-16 sm:text-2xl',
              invalid
                ? 'border-[var(--color-danger-button-border)] text-[var(--color-text-primary)]'
                : isActive
                  ? 'border-[var(--color-accent)] shadow-[0_0_0_2px_var(--color-focus-ring)]'
                  : hasValue
                    ? 'border-[var(--color-border-strong)] text-[var(--color-text-primary)]'
                    : 'border-[var(--color-border)] text-transparent',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={index} className={slotClasses}>
                {digit || ' '}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default OtpSlotField;
